<template>
  <div v-if="asset" class="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-7xl mx-auto px-6 py-8">
    <div class="col-span-full">
      <div class="lg:flex lg:flex-row lg:items-center lg:justify-between">
        <h2 class="font-bold text-2xl text-surface-700">
          {{
            $t('pages.admin.assets.assets.asset.devices.asset_devices_view.showing_devices_for_asset_x', {asset: asset.name})
          }}
        </h2>
        <div class="flex items-center mt-8 lg:mt-0 gap-3">
          <IconField icon-position="left" class="ml-2">
            <InputIcon>
              <IconSearch class="text-surface-400 dark:text-surface-600 h-4 w-4"/>
            </InputIcon>
            <InputText
                @change="onSearch"
                :placeholder="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.search_devices')"
                v-model="params.filter.search"
                @keyup="onSearch"
            />
          </IconField>
          <Button :label="$t('pages.admin.assets.assets.asset.devices.asset_devices_view.undo_changes')" @click="undo" v-if="$auth.hasPermission('update:firmware-regulations')"/>
          <Button data-cy="asset-device-save-button" :label="$t('common.save')" @click="submit" v-if="$auth.hasPermission('update:firmware-regulations')"/>
        </div>
      </div>
    </div>

    <Card class="lg:col-span-4">
      <template #title>
        {{
          $t('pages.admin.assets.assets.asset.devices.asset_devices_view.add_devices_to_this_asset')
        }}
      </template>
      <template #subtitle>
        {{
          $t('pages.admin.assets.assets.asset.devices.asset_devices_view.select_a_device_from_list')
        }}
      </template>
      <template #content>
        <PickList
            v-model="devices"
            :show-source-controls="false"
            :show-target-controls="false"
            listStyle="height:342px"
            dataKey="id"
            breakpoint="1024px"
        >
          <template #sourceheader>
            <div class="flex flex-row justify-between items-center">
              <div class="flex-1">
                <h5>{{ $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.available_devices') }}</h5>
              </div>
              <ProgressSpinner v-if="loading" class="h-4 w-4"/>
              <span v-if="!loading" class="text-sm font-medium text-surface-400 block">
                {{ $t('pages.admin.assets.assets.asset.devices.asset_devices_view.showing_x_of_x', { count: devices[0]?.length, total: totalRecords?.toLocaleString() ?? 0 }) }}
              </span>
            </div>
          </template>
          <template #targetheader>
            {{ $t('pages.admin.assets.assets.asset.devices.asset_devices_view.devices_on_asset') }}
          </template>

          <template #item="{item}">
            <div class="flex flex-wrap items-center gap-4"  style="height: 40px">
              <img class="w-[4rem] shrink-0 rounded-sm" v-if="getImageUrl(item)" :src="getImageUrl(item)"
                   :alt="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.image_of_the_device_type')"
              />
              <IconRouter v-else :stroke-width="2" class="text-surface-400"/>
              <div class="flex-1">
                <h6 class="font-bold text-base text-surface-700 tracking-wide">
                  {{ item.serial_number }}
                </h6>
                <span class="text-sm text-surface-500">
                  {{ item.company.name }}
                </span>
              </div>
            </div>
          </template>
        </PickList>
      </template>
    </Card>
  </div>
</template>

<script>
import {mapStores} from "pinia";
import {useAssetsStore} from "@/stores/assets.js";
import {useDevicesStore} from "@/stores/devices.js";
import {useForm} from "laravel-precognition-vue";
import {FilterMatchMode} from "primevue/api";
import _ from "lodash";
import FormGroup from "@/Components/FormGroup.vue";
import {IconRouter, IconSearch, IconStar} from "@tabler/icons-vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import {resolveDeviceImageUrl} from "@/Composables/DeviceImage.js";

export default {
  name: "`AssetDevicesView`",
  components: {IconRouter, IconSearch, IconStar, IconField, FormGroup},
  data() {
    return {
      totalRecords: 0,
      loading: true,
      devices: [],
      selectedDevices: [],
      x: undefined,
      filters: {
        global: {
          value: null,
          matchMode: FilterMatchMode.CONTAINS
        },
      },
      params: {
        page: 1, // used to limit devices
        per_page: 20,
        include: ['company', 'deviceType', 'deviceModel'],
        filter: {
          search: null,
          asset_id: null,
          has_asset: 0,
        }
      },
    }
  },
  computed: {
    ...mapStores(useAssetsStore),
    ...mapStores(useDevicesStore),
    asset() {
      return this.$attrs.asset;
    },
  },
  methods: {
    async undo() {
      this.devices = [];
      this.selectedDevices = this.asset.devices
      this.params.filter.search = null
      await this.loadData();
      },
    getImageUrl(device) {
      return resolveDeviceImageUrl(device);
    },
    async submit() {
      try {
        this.assignDevicesForm.device_ids = this.devices[1].map(r => r.id);
        let response = await this.assignDevicesForm.submit();
        if (response.status == 200 && response.data) { // get updated list
          this.asset.devices = response.data;
        }
        await this.loadData();
        this.$toast.add({
          severity: 'success',
          summary: this.$t('common.complete'),
          detail: this.$t('pages.admin.assets.assets.asset.devices.asset_devices_view.devices_linked_to_asset'),
          life: 3000
        });
        this.$emit('refresh-asset')
      } catch (e) {
        this.$toast.add({
          severity: 'info',
          summary: this.$t('common.cancelled'),
          detail: this.$t('pages.admin.assets.assets.asset.devices.asset_devices_view.devices_were_not_linked_to_asset'),
          life: 3000
        });
        console.log(e);
      }
    },
    async loadData() {
      // take a note of the selected devices before overwriting
      if (this.devices[1]) this.selectedDevices = this.devices[1];
      this.loading = true;
      this.params.filter.company_id = this.asset.company_id
      let devices = await this.devicesStore.indexDevices(this.params);
      this.devices = [devices.data, this.selectedDevices]
      this.totalRecords = devices.total;
      this.loading = false;
    },
    onSearch: _.debounce(
        function () {
          if (this.params.filter.search === '') {
            this.params.filter.search = null;
          }
          this.loadData();
        },
        500
    ),
  },
  async mounted() {
  },
  watch: {
    'asset': {
      async handler(newValue, oldValue) {
        if (newValue) {
          try {
            this.devices = [];
            this.selectedDevices = this.asset.devices
            await this.loadData();

            let selected = this.devices[1]; // selected portion of the array
            this.assignDevicesForm = useForm('post', `/assets/${this.asset.id}/devices`, {
              device_ids: selected.map(d => d.id)
            });
          } catch (e) {
            console.log(e);
            this.devices = null;
            this.selectedDevices = null;
            this.assignDevicesForm = null;
          }
        }
      },
      immediate: true,
    }
  },
}
</script>
